import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';
import '@fontsource/inter';
import '@fontsource/fira-code';
import 'virtual:uno.css';
import '@/assets/styles/unocss-utilities.css';
import { createPinia } from 'pinia';
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import * as locale from 'element-plus/dist/locale/es.js';
import App from './App.vue';
import router from './router';
import api from '@/services/api';
import { createAuthErrorHandler } from '@/services/auth_interceptor.js';
import { useAuthStore } from '@/views/auth/auth_store.js';
import DevicePlugin from '@/shared/plugins/device_plugin.js';

import 'primeicons/primeicons.css';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import Aura from '@primeuix/themes/aura';
import Tooltip from 'primevue/tooltip';
import ToastService from 'primevue/toastservice';
import Select from 'primevue/select';

import ElementoHeader from '@/shared/components/elemento_header.vue';

import RequestLabel from '@/views/requests/request_edit/components/request_label.vue';
import RequestTermoEncogibleLabel from '@/views/requests/request_edit/components/request_termoEncogible_label.vue';
import RequestFlexiblePackaging from '@/views/requests/request_edit/components/request_flexible_packaging.vue';
import RequestConsumibleLabel from '@/views/requests/request_edit/components/request_consumible_label.vue';
import RequestCajas from '@/views/requests/request_edit/components/request_cajas.vue';
import RequestOffset from '@/views/requests/request_edit/components/request_offset.vue';
import RequestBook from '@/views/requests/request_edit/components/request_book.vue';
import RequestBookInner from '@/views/requests/request_edit/components/request_book_inner.vue';
import RequestExtrusion from '@/views/requests/request_edit/components/request_extrusion.vue';
import RequestBolsasFuelle from '@/views/requests/request_edit/components/request_bolsas_fuelle.vue';
import RequestBolsasBox from '@/views/requests/request_edit/components/request_bolsas_box.vue';

import PriceLabel from '@/views/prices/prices_edit/components/price_label.vue';
import PriceTermoEncogibleLabel from '@/views/prices/prices_edit/components/price_termoEncogible_label.vue';
import PriceFlexiblePackaging from '@/views/prices/prices_edit/components/price_flexible_packaging.vue';
import PriceConsumibleLabel from '@/views/prices/prices_edit/components/price_consumible_label.vue';
import PriceCajas from '@/views/prices/prices_edit/components/price_cajas.vue';
import PriceOffset from '@/views/prices/prices_edit/components/price_offset.vue';
import PriceBook from '@/views/prices/prices_edit/components/price_book.vue';
import PriceBookInner from '@/views/prices/prices_edit/components/price_book_inner.vue';
import PriceExtrusion from '@/views/prices/prices_edit/components/price_extrusion.vue';
import PriceBolsasFuelle from '@/views/prices/prices_edit/components/price_bolsas_fuelle.vue';
import PriceBolsasBox from '@/views/prices/prices_edit/components/price_bolsas_box.vue';

import WorkOrderLabel from '@/views/work_orders/work_order_edit/components/work_order_label.vue';
import WorkOrderTermoEncogibleLabel from '@/views/work_orders/work_order_edit/components/work_order_termoEncogible_label.vue';
import WorkOrderFlexiblePackaging from '@/views/work_orders/work_order_edit/components/work_order_flexible_packaging.vue';
import WorkOrderConsumibleLabel from '@/views/work_orders/work_order_edit/components/work_order_consumible_label.vue';
import WorkOrderCajas from '@/views/work_orders/work_order_edit/components/work_order_cajas.vue';
import WorkOrderOffset from '@/views/work_orders/work_order_edit/components/work_order_offset.vue';
import WorkOrderBook from '@/views/work_orders/work_order_edit/components/work_order_book.vue';
import WorkOrderBookInner from '@/views/work_orders/work_order_edit/components/work_order_book_inner.vue';
import WorkOrderBolsasFuelle from '@/views/work_orders/work_order_edit/components/work_order_bolsas_fuelle.vue';
import WorkOrderBolsasBox from '@/views/work_orders/work_order_edit/components/work_order_bolsas_box.vue';
import WorkOrderExtrusion from '@/views/work_orders/work_order_edit/components/work_order_extrusion.vue';

import TechnicalSpecificationLabel from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_label.vue';
import TechnicalSpecificationTermoEncogibleLabel from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_termoEncogible_label.vue';
import TechnicalSpecificationFlexiblePackaging from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_flexible_packaging.vue';
import TechnicalSpecificationConsumibleLabel from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_consumible_label.vue';
import TechnicalSpecificationCajas from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_cajas.vue';
import TechnicalSpecificationOffset from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_offset.vue';
import TechnicalSpecificationBook from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_book.vue';
import TechnicalSpecificationBookInner from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_book_inner.vue';
import TechnicalSpecificationBolsasFuelle from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_bolsas_fuelle.vue';
import TechnicalSpecificationBolsasBox from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_bolsas_box.vue';
import TechnicalSpecificationExtrusion from '@/views/technical_specifications/technical_specification_edit/components/technical_specification_extrusion.vue';

import SettingDetailProduct from '@/views/setting/components/custom_product/setting_detail_product.vue';
import SettingEditProduct from '@/views/setting/components/custom_product/setting_edit_product.vue';
import SettingCreateProduct from '@/views/setting/components/custom_product/setting_create_product.vue';
import { formatDecimal } from '@/shared/utils/format_decimal.js';

import './assets/styles/main.css';
const app = createApp(App);
const cliente = import.meta.env.VITE_CLIENT_ID;

async function bootstrap() {
  const urlEnv = import.meta.env.VITE_URL_BASE_API || '/api';
  const res = await fetch(`${urlEnv}/env`);

  if (res.environment && res.environment === 'production') {
    Sentry.init({
      app,
      dsn: 'https://d5611f603a110d021471483b45610924@o4509050507493376.ingest.us.sentry.io/4509288013627392',
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,

      tracesSampleRate: 0.1,
      beforeSend(event) {
        event.tags = {
          ...event.tags,
          cliente,
        };

        return event;
      },
      environment: res.environment || import.meta.env.NODE_ENV,
    });
  }

  app.config.globalProperties.$formatDecimal = formatDecimal;

  const pinia = createPinia();
  app.use(pinia);
  app.use(router);
  app.use(ElementPlus, { size: 'small', locale });

  // Inicializar detección de dispositivos
  const { useDeviceDetectionStore } = await import('@/stores/device_detection.js');
  const deviceStore = useDeviceDetectionStore();
  deviceStore.detectDevice();
  deviceStore.initializeListeners();

  // Instalar plugin de dispositivo
  app.use(DevicePlugin);

  // Configurar interceptores de API después de inicializar Pinia
  const authStore = useAuthStore();

  api.interceptors.request.use(
    (config) => {
      const token = JSON.parse(localStorage.getItem('token'));
      if (token) {
        config.headers['authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (err) => {
      return Promise.reject(err);
    },
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    createAuthErrorHandler({
      api,
      closeSession: () => authStore.closeSession(),
      redirectToLogin: () => router.replace('/'),
    }),
  );

  app.component('ElementoHeader', ElementoHeader);
  app.component('RequestLabel', RequestLabel);
  app.component('RequestTermoEncogibleLabel', RequestTermoEncogibleLabel);
  app.component('RequestConsumibleLabel', RequestConsumibleLabel);
  app.component('RequestFlexiblePackaging', RequestFlexiblePackaging);
  app.component('RequestCajas', RequestCajas);
  app.component('RequestOffset', RequestOffset);
  app.component('RequestBook', RequestBook);
  app.component('RequestExtrusion', RequestExtrusion);
  app.component('RequestBookInner', RequestBookInner);
  app.component('RequestBolsasFuelle', RequestBolsasFuelle);
  app.component('RequestBolsasBox', RequestBolsasBox);

  app.component('PriceCajas', PriceCajas);
  app.component('PriceOffset', PriceOffset);
  app.component('PriceConsumibleLabel', PriceConsumibleLabel);
  app.component('PriceLabel', PriceLabel);
  app.component('PriceFlexiblePackaging', PriceFlexiblePackaging);
  app.component('PriceTermoEncogibleLabel', PriceTermoEncogibleLabel);
  app.component('PriceBook', PriceBook);
  app.component('PriceBookInner', PriceBookInner);
  app.component('PriceExtrusion', PriceExtrusion);
  app.component('PriceBolsasFuelle', PriceBolsasFuelle);
  app.component('PriceBolsasBox', PriceBolsasBox);

  app.component('WorkOrderCajas', WorkOrderCajas);
  app.component('WorkOrderOffset', WorkOrderOffset);
  app.component('WorkOrderConsumibleLabel', WorkOrderConsumibleLabel);
  app.component('WorkOrderLabel', WorkOrderLabel);
  app.component('WorkOrderTermoEncogibleLabel', WorkOrderTermoEncogibleLabel);
  app.component('WorkOrderFlexiblePackaging', WorkOrderFlexiblePackaging);
  app.component('WorkOrderBook', WorkOrderBook);
  app.component('WorkOrderBookInner', WorkOrderBookInner);
  app.component('WorkOrderBolsasFuelle', WorkOrderBolsasFuelle);
  app.component('WorkOrderBolsasBox', WorkOrderBolsasBox);
  app.component('WorkOrderExtrusion', WorkOrderExtrusion);

  app.component('TechnicalSpecificationCajas', TechnicalSpecificationCajas);
  app.component('TechnicalSpecificationOffset', TechnicalSpecificationOffset);
  app.component('TechnicalSpecificationConsumibleLabel', TechnicalSpecificationConsumibleLabel);
  app.component('TechnicalSpecificationLabel', TechnicalSpecificationLabel);
  app.component('TechnicalSpecificationFlexiblePackaging', TechnicalSpecificationFlexiblePackaging);
  app.component(
    'TechnicalSpecificationTermoEncogibleLabel',
    TechnicalSpecificationTermoEncogibleLabel,
  );
  app.component('TechnicalSpecificationBook', TechnicalSpecificationBook);
  app.component('TechnicalSpecificationBookInner', TechnicalSpecificationBookInner);
  app.component('TechnicalSpecificationBolsasFuelle', TechnicalSpecificationBolsasFuelle);
  app.component('TechnicalSpecificationBolsasBox', TechnicalSpecificationBolsasBox);
  app.component('TechnicalSpecificationExtrusion', TechnicalSpecificationExtrusion);

  app.component('SettingDetailProduct', SettingDetailProduct);
  app.component('SettingEditProduct', SettingEditProduct);
  app.component('SettingCreateProduct', SettingCreateProduct);

  // Como el plugin es asíncrono,
  // debo instanciar mi app luego
  app.use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        cssLayer: {
          name: 'primevue',
          order: 'theme, base, primevue',
        },
        darkModeSelector: '.dark',
      },
    },
  });
  app.use(ConfirmationService);
  app.use(ToastService);
  app.directive('tooltip', Tooltip);

  app.mount('#app');
}

bootstrap();
